import { useService } from 'react-reservice';

import { ILanguage } from '@aqx/i18n';

import { ELoginAs } from '@aqx-core/common/api/auth';
import { EMainProductCodeType, EMarketTimeManagementType, ETenantId, ICompany } from '@aqx-core/common/api/config';
import { TAppLanguageISOCountryCode } from '@aqx-core/common/i18n';
import { APP_LANGUAGES, APP_LANGUAGES_MAP, HANTEC_LANGUAGES_CODE } from '@aqx-core/common/i18n/languages';
import { DEFAULT_CANDLE_STICK_COLORS_RGB } from '@aqx-core/common/theme/colors';
import { validateCompanyAccess } from '@aqx-core/common/utils/company-access';

import { pickProperties } from '../utils';
import { TENANT_THEMES_MAP } from './constants';
import { companyService } from './service';
import { getRegistrationUrl } from './utils';

export const useCompanyProfile = () => useService(companyService, (context) => context.companyProfile);
export const useCompanyLoading = () => useService(companyService, (context) => context.loading);
export const useCompanyContact = () => useService(companyService, (context) => context.companyContact);

export const useCompanyTenantId = () => useService(companyService, (context) => context.companyProfile?.tenantId);

export const useCompanyProfileWithKeys = <K extends keyof ICompany>(keys: K[]) => {
  return useService(companyService, (context) => {
    return pickProperties(context.companyProfile || ({} as ICompany), keys);
  });
};

export const useCompanyLangInitialised = () =>
  useService(companyService, (context) => context.isLangInitialised || false);
export const useCompanyDefaultLanguage = () =>
  useService(companyService, (context) => {
    if (context.companyProfile) {
      return pickProperties(context.companyProfile, ['defaultLanguage']);
    }
    return { defaultLanguage: null };
  });

export const useCompanySupportedLanguage = () =>
  useService(companyService, (context) => {
    if (!context.companyProfile) {
      return APP_LANGUAGES.map((lang) => lang.value);
    }

    const { supportedLanguages, defaultLanguage } = context.companyProfile || {};
    // If no supported languages are set, return all available languages for new tenants
    if ((!supportedLanguages || supportedLanguages.length === 0) && defaultLanguage) {
      return [defaultLanguage];
    }
    return supportedLanguages;
  });

export const useCompanyName = () => useService(companyService, (context) => context.companyProfile?.name || '');

export const useCompanyAccess = () =>
  useService(companyService, (context) => validateCompanyAccess(context.companyProfile));

export const useCompanyMarketTimeIsServer = () =>
  useService(
    companyService,
    (context) => context.companyProfile?.marketTimeManagement === EMarketTimeManagementType.SERVER
  );

export const useShowCompanyContact = () =>
  useService(companyService, ({ companyContact }) => {
    const isNull = (value: unknown) => value === null || value === undefined;
    const contactUsData = [
      isNull(companyContact?.mobile),
      isNull(companyContact?.website),
      isNull(companyContact?.email),
      isNull(companyContact?.businessHoursTimeZone),
      isNull(companyContact?.businessHours),
      isNull(companyContact?.socialMedia)
    ];
    return contactUsData.includes(false);
  });

export const useCompanyIntegration = () =>
  useService(companyService, ({ companyProfile }) => {
    return companyProfile
      ? companyProfile.currentMainProductCode || companyProfile.mainProductCodeList[0]
      : EMainProductCodeType.METATRADER4;
  });

export const useCompanyIntegrationLabel = () => {
  const integrationLabel: Partial<Record<EMainProductCodeType, string>> = {
    [EMainProductCodeType.METATRADER4]: 'MT4',
    [EMainProductCodeType.METATRADER5]: 'MT5',
    [EMainProductCodeType.CENTROID]: 'CENTROID',
    [EMainProductCodeType.HORIZON]: 'HORIZON'
  };
  const companyIntegration = useCompanyIntegration();
  return integrationLabel[companyIntegration];
};

export const useCompanyIntegrationType = () => {
  const companyIntegration = useCompanyIntegration();
  return {
    isCentroid: companyIntegration === EMainProductCodeType.CENTROID,
    isMT5: companyIntegration === EMainProductCodeType.METATRADER5,
    isMT4: companyIntegration === EMainProductCodeType.METATRADER4,
    isHorizon: companyIntegration === EMainProductCodeType.HORIZON,
    isMetatrader:
      companyIntegration === EMainProductCodeType.METATRADER4 || companyIntegration === EMainProductCodeType.METATRADER5
  };
};

export const useCompanyHideMarginRequirement = () => {
  return useService(companyService, (context) => {
    if (context.companyProfile) {
      const { hasEstimatedMarginRequired } = validateCompanyAccess(context.companyProfile);
      return !hasEstimatedMarginRequired;
    }
    return false;
  });
};

/** Returns whether to display notification alerts after order actions (placing/editing orders/close orders) */
export const useCompanyShowNotificationOnOrderActions = () => {
  const { isCentroid } = useCompanyIntegrationType();
  return !isCentroid;
};

/** For Centroid integration, notifications are handled via websocket messages */
export const useCompanyShowNotificationOnSocketMessage = () => {
  const { isCentroid } = useCompanyIntegrationType();
  return isCentroid;
};

export const useCompanyShowTradingViewChart = () => {
  return useService(companyService, ({ companyProfile }) => {
    if (companyProfile) {
      const { hasTradingViewChart } = validateCompanyAccess(companyProfile);
      return hasTradingViewChart;
    }
    return false;
  });
};

export const useServerTimezoneOffset = () =>
  useService(companyService, ({ companyProfile }) => companyProfile?.serverTimezone);

export const useCompanyCustomThemeTemplate = () => {
  return useService(companyService, ({ companyProfile }) => {
    const themeTemplate = companyProfile?.tenantId
      ? TENANT_THEMES_MAP[companyProfile.tenantId] || TENANT_THEMES_MAP.default
      : TENANT_THEMES_MAP.default;
    return { themeTemplate };
  });
};

export const useCompanyDefaultCandlestickColors = () => {
  return useService(companyService, ({ companyProfile }) => ({
    positive: companyProfile?.profitColourCode || DEFAULT_CANDLE_STICK_COLORS_RGB.positive,
    negative: companyProfile?.lossColourCode || DEFAULT_CANDLE_STICK_COLORS_RGB.negative
  }));
};

export const useCompanyLanguageOptions = () => {
  const languageOptions = useService(companyService, ({ companyProfile }) => {
    let langCodes = companyProfile?.supportedLanguages;
    // WT-8792: Temporary hardcode until blueberry or hantec updates their supported languages
    if (!langCodes || langCodes.length === 0) {
      if (companyProfile?.tenantId === ETenantId.HANTEC) {
        // WT-8803: Hardcode language sequence for hantec
        // hantec (in prod/uat)
        langCodes = HANTEC_LANGUAGES_CODE;
      }
    }

    if (!langCodes) {
      return APP_LANGUAGES;
    }

    return langCodes.reduce((prev: ILanguage<TAppLanguageISOCountryCode>[], langCode: TAppLanguageISOCountryCode) => {
      if (!APP_LANGUAGES_MAP[langCode]) {
        return prev;
      }
      prev.push(APP_LANGUAGES_MAP[langCode]);
      return prev;
    }, []);
  });

  return languageOptions;
};

export const useCompanyRegistrationUrl = (selectedTab: ELoginAs | undefined) => {
  return useService(companyService, ({ companyProfile }) => getRegistrationUrl(companyProfile, selectedTab));
};

// WT-10463: Determine whether or not order buttons (i.e. Buy and Sell) should be reversed
export const useCompanyReverseOrderBtns = () => {
  const tenantId = useCompanyTenantId();
  return tenantId === ETenantId.CENTROID || tenantId === ETenantId.TINSHING;
};

export const useFirstDemoTenantProduct = () => {
  return useService(companyService, ({ companyProfile }) => {
    if (companyProfile) {
      const { firstDemoTenantProduct } = companyProfile;
      const isCentroidDemo = firstDemoTenantProduct === EMainProductCodeType.CENTROID;

      return {
        isCentroidDemo
      };
    }

    return {
      isCentroidDemo: false
    };
  });
};
